import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { salesApi } from '../salesApi';
import { useT } from '../salesI18n';

const Ctx = createContext({ leadsWithNew: [], running: [], inbox: { items: [], unread: 0 }, refreshInbox: () => {}, markRead: () => {}, markAllRead: () => {} });

// Destino ao clicar numa notificação (toast ou sininho)
export const notifTarget = (n) => (n.kind === 'simulation' ? `/leads/${n.lead_id}?tab=simulation` : n.kind === 'estimate' ? `/leads/${n.lead_id}` : `/leads/${n.lead_id}?tab=proposal`);
const isoToBR = (s) => String(s || '').replace(/(\d{4})-(\d{2})-(\d{2})/g, '$3/$2/$1');
export const notifMessage = (t, n) => {
  if (n.kind === 'simulation') return t.simReadyFor(n.lead_name);
  if (n.kind === 'estimate') return t.notifEstimate(n.lead_name);
  if (n.kind === 'accepted') return t.notifAccepted(n.lead_name, n.proposal_number);
  if (n.kind === 'interest') return t.notifInterest(n.lead_name, n.proposal_number);
  if (n.kind === 'project_stage') return t.notifProjectStage(n.lead_name, t.projectStages[n.detail] || n.detail);
  if (n.kind === 'project_scheduled') return t.notifProjectScheduled(n.lead_name);
  if (n.kind === 'project_rescheduled') return t.notifProjectRescheduled(n.lead_name);
  return t.notifViewed(n.lead_name, n.proposal_number);
};
// Linha secundária (etapa já está no título; agendamento mostra "PM2 · 15/07/2026")
export const notifDetail = (n) => (n.kind === 'project_stage' ? null : n.kind?.startsWith('project_') ? isoToBR(n.detail) : n.detail || null);
const ackOf = (n) => (n.kind === 'simulation' ? salesApi.post(`/simulations/${n.id}/ack`) : salesApi.post(`/notifications/${n.id}/ack`));

// Polling de simulações prontas + eventos: toast com "Ver", badge nos leads e histórico (sininho)
export const SalesNotificationsProvider = ({ children }) => {
  const { t } = useT();
  const navigate = useNavigate();
  const [state, setState] = useState({ leadsWithNew: [], running: [] });
  const [inbox, setInbox] = useState({ items: [], unread: 0 });
  const shown = useRef(new Set());

  const refreshInbox = useCallback(async () => {
    try {
      const { data } = await salesApi.get('/notifications');
      setInbox({ items: data.items || [], unread: data.unread || 0 });
    } catch (e) { /* silencioso */ }
  }, []);

  const markRead = useCallback(async (n) => {
    setInbox((s) => ({ items: s.items.map((x) => (x.id === n.id ? { ...x, read: true } : x)), unread: Math.max(0, s.unread - (n.read ? 0 : 1)) }));
    try { await ackOf(n); } catch (e) { /* silencioso */ }
  }, []);

  const markAllRead = useCallback(async () => {
    setInbox((s) => ({ items: s.items.map((x) => ({ ...x, read: true })), unread: 0 }));
    try { await salesApi.post('/notifications/read-all'); } catch (e) { /* silencioso */ }
  }, []);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await salesApi.get('/notifications/pending');
        if (!alive) return;
        setState({ leadsWithNew: data.leads_with_new || [], running: data.running || [] });
        const ackThen = (n) => () => { ackOf(n).catch(() => {}); refreshInbox(); };
        // no máximo 3 toasts por rodada — o resto fica só no sininho (não lido)
        const fresh = [...(data.ready || []).map((j) => ({ ...j, kind: 'simulation', created_at: j.completed_at || j.created_at })), ...(data.events || [])].filter((n) => !shown.current.has(n.id));
        fresh.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
        fresh.forEach((n) => shown.current.add(n.id));
        fresh.slice(0, 3).forEach((n) => {
          const ack = ackThen(n);
          if (n.kind === 'simulation') {
            if (window.location.pathname.endsWith(`/leads/${n.lead_id}`)) return;
            toast.success(t.simReady, {
              description: t.simReadyFor(n.lead_name),
              duration: 7000, onDismiss: ack, onAutoClose: ack,
              action: { label: t.view, onClick: () => { ack(); navigate(notifTarget(n)); } },
            });
            return;
          }
          (n.kind === 'viewed' ? toast.info : toast.success)(notifMessage(t, n), {
            description: notifDetail(n) || undefined, duration: n.kind === 'viewed' ? 5000 : 7000, onDismiss: ack, onAutoClose: ack,
            action: { label: t.view, onClick: () => { ack(); navigate(notifTarget(n)); } },
          });
        });
        refreshInbox();
      } catch (e) { /* silencioso */ }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { alive = false; clearInterval(id); };
  }, [navigate, t, refreshInbox]);

  return <Ctx.Provider value={{ ...state, inbox, refreshInbox, markRead, markAllRead }}>{children}</Ctx.Provider>;
};

export const useSalesNotifications = () => useContext(Ctx);
